import Typography from "@portal/components/atoms/v2/typography";
import PortfolioSelect from "@portal/components/molecules/portfolioSelect";
import BankAccountSelect from "@portal/components/molecules/bankAccountsSelect";
import { OnAccountChange } from "../../../components/handlers";
import { IBankAccount } from "@portal/types/api/IBankAccount";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { IPortfolio } from "@portal/types/api/IPortfolio";
import { IAccountProps } from "../types";
import { useMemo, useState } from "react";
import AddCashAccountButton from "@portal/components/molecules/addCashAccountButton";
import { DialogComponent } from "@portal/components/organisms/DialogComponent";
import Icon from "@portal/components/atoms/icons";
import NewCashAccount from "@portal/components/organisms/newCashAccount";
import useFilteredPortfolios from "@portal/hooks/useFilteredPortfolios";
import Link from "next/link";
import { RadioGroup } from "@headlessui/react";
import { setSavingsAgreementOtherCashAccount } from "@portal/redux/reducers/ui/createSavingsAgreement";
import RadioComponent from "@portal/components/atoms/v2/radioComponent";

export const AccountOptions = ({
  selectedPortfolio,
  bankAccounts,
  selectedBankAccountNumber,
  otherBankAccountNumber,
  isDefaultLanguage,
  isAskDisabled,
  createAskAccount,
  dispatch,
  setShowCreateAskModal,
  showOtherBankAccount,
}: IAccountProps) => {
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const selectedBankAccount = useMemo(
    () => bankAccounts?.find(acc => acc.accountNumber === selectedBankAccountNumber),
    [bankAccounts, selectedBankAccountNumber],
  );
  const otherBankAccount = useMemo(
    () => bankAccounts?.find(acc => acc.accountNumber === otherBankAccountNumber),
    [bankAccounts, otherBankAccountNumber],
  );
  const [showAddBankAccountDialog, setShowAddBankAccountDialog] = useState(false);
  const { data: portfolios, isLoading: portfoliosIsLoading } = useFilteredPortfolios({ hidePortfoliosWithOnlyReadAccess: true });
  const [sameBankAccount, setSameBankAccount] = useState(true);

  const closeAddBankAccountDialog = () => {
    setShowAddBankAccountDialog(false);
  };

  const OnOtherAccountChange = (account?: IBankAccount) => {
    dispatch(setSavingsAgreementOtherCashAccount(account?.accountNumber ?? undefined));
  };

  return (
    <div className="space-y-2">
      <div>
        <div className="flex space-x-1">
          <Typography variant="BodySpan">{texts.PAGE_16_ACCOUNT_A}</Typography>
          <Link href="https://norne.no/fond/ask" target="_blank" className="text-blueHighlighted">
            <Typography variant="BodySpan">{texts.PAGE_16_ACCOUNT_LINK_TEXT}</Typography>
          </Link>
          <Typography variant="BodySpan">{texts.PAGE_16_ACCOUNT_B}</Typography>
        </div>
        <PortfolioSelect
          label={""}
          value={selectedPortfolio || ""}
          onChange={(newValue: IPortfolio | "") => {
            if (newValue) OnAccountChange(newValue, dispatch, setShowCreateAskModal);
          }}
          typographyVariant="Body"
          spaced
          isAskDisabled={isAskDisabled}
          createAskAccount={createAskAccount}
          portfolios={portfolios ?? []}
          portfoliosIsLoading={portfoliosIsLoading}
        />
      </div>
      <div>
        <div className="flex">
          <Typography variant="Body">{texts.PAGE_16_DEPOSIT}</Typography>
        </div>
        <BankAccountSelect
          value={selectedBankAccount || ""}
          onChange={(item: IBankAccount | "") => {
            if (item) OnAccountChange(item, dispatch, setShowCreateAskModal);
          }}
          typographyVariant="Body"
          includeOtherAccount={false}
          spaced
        />
        <div className="mt-1">
          <AddCashAccountButton handleClick={() => setShowAddBankAccountDialog(true)} />
        </div>
      </div>

      {showOtherBankAccount && (
        <div className="space-y-2">
          <div>
            <div className="flex">
              <Typography variant="Body">{texts.PAGE_16_OTHER_ACCOUNT_LABEL}</Typography>
            </div>
            <RadioGroup
              value={sameBankAccount}
              onChange={(val: boolean) => {
                if (val) {
                  OnOtherAccountChange(undefined);
                }
                setSameBankAccount(val);
              }}
              className="flex space-x-4"
            >
              <RadioComponent id="otherAccountYes" value={true} text={texts.PAGE_16_OTHER_ACCOUNT_YES} large />
              <RadioComponent id="otherAccountNo" value={false} text={texts.PAGE_16_OTHER_ACCOUNT_NO} large />
            </RadioGroup>
          </div>
          {!sameBankAccount && (
            <div className="ml-4">
              <div className="flex mt-2.5">
                <Typography variant="Body">{texts.PAGE_16_DEPOSIT_OTHER}</Typography>
              </div>
              <BankAccountSelect
                value={otherBankAccount || ""}
                onChange={(item: IBankAccount | "") => {
                  if (item) OnOtherAccountChange(item);
                }}
                typographyVariant="Body"
                includeOtherAccount={false}
                spaced
              />
            </div>
          )}
        </div>
      )}

      <DialogComponent
        open={showAddBankAccountDialog}
        onClose={() => closeAddBankAccountDialog()}
        panelClassName="top-5 lg:top-1/4 w-96 lg:w-[600px] rounded-lg bg-white border border-greyBorder p-4"
      >
        <div className="flex flex-row justify-end">
          <button onClick={() => closeAddBankAccountDialog()} className="focus:outline-1 focus:outline-blueLight">
            <Icon name="xMarkIcon" size="xsmall" />
          </button>
        </div>
        <NewCashAccount close={closeAddBankAccountDialog} />
      </DialogComponent>
    </div>
  );
};
